<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { Permission } from '../../../types/auth';
import { CommonFunctions } from '../../../utils/CommonFunctions';
import { Tabs } from '../../table/table';
import IconComponent from './IconComponent.vue';
import UserActivityLog from './UserActivityLog.vue';

const props = withDefaults(defineProps<{
  tabs: Tabs[]
  permissions?: Permission[]
  initalTab?: string
  record?: any
  reload?: boolean
}>(), {
  initalTab: 'activity-log',
  reload: false
})

const emit = defineEmits<{
  (e: 'tabChanged', tabId: string): void
}>()

const activeTab = ref(props.initalTab)

const tabList: Tabs[] = [
  { id: 'activity-log', label: 'Activity Log', icon: 'Activity', checkPermission: false, permission: 'View Activity Log' },
  { id: 'audit-log', label: 'Audit Log', icon: 'Activity', checkPermission: false, permission: 'View Activity Log' },
]

// Sample data for audit logs
const auditLogs = [
  {
    icon: '✏️',
    action: 'Profile Edit',
    details: 'Updated email address',
    user: 'John Doe',
    date: '2024-01-15 14:30'
  },
  {
    icon: '💰',
    action: 'Credit Limit Change',
    details: 'Increased from $5,000 to $7,500',
    user: 'Jane Smith',
    date: '2024-01-10 11:20'
  },
  {
    icon: '🔒',
    action: 'Status Change',
    details: 'Changed status to Active',
    user: 'Admin User',
    date: '2024-01-05 09:15'
  }
]

onMounted(() => {
  switchTab(props.initalTab)
})

const switchTab = (tabId: string) => {
  activeTab.value = tabId
  emit('tabChanged', tabId)
}
const isCollapsed = ref(false)

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value
}

watch(() => props.reload, () => {
    switchTab(props.initalTab)
}, { immediate: true })

</script>

<template>
  <div class="relative flex h-full">
    <!-- Collapse Toggle Button -->
    <button
       v-if="!isCollapsed"
      @click="toggleCollapse"
      class="absolute -left-3 top-8 w-6 h-20 flex items-center justify-center bg-white dark:bg-gray-800 rounded-l-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group z-10"
    >
      <div class="flex items-center">
        <span 
          class="text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200 transition-transform"
        >
          →
        </span>
      </div>
    </button>

    <div 
      class="flex-1 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto transition-all duration-300"
      :class="[isCollapsed ? 'w-10' : 'w-96']"
    >
      <template v-if="!isCollapsed">
        <div class="h-full flex flex-col" v-if="record">
          <!-- Header -->
          <div class="p-6 border-b border-gray-200 dark:border-gray-700">
            <div class="flex items-center gap-4">
              <div class="w-16 h-16 rounded-full flex border items-center justify-center text-3xl">
                <NoImage v-if="!record.image" class="w-10" />
              </div>
              <div>
                <h2 class="text-xl font-semibold text-gray-900 dark:text-white">{{ record.code }}</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ record.name }}</p>
              </div>
            </div>
          </div>

          <!-- Tabs -->
           <!-- Tabs Header -->
            <div class="border-b border-gray-200 dark:border-gray-700" id="form-extra-info-tabs">
                <div class="flex">
                <template v-for="tabpage in tabs">
                    <button
                    :key="tabpage.id"
                    v-if="tabpage.checkPermission == false || (tabpage.permission && CommonFunctions.hasPermission(props.permissions, tabpage.permission))"
                    @click="switchTab(tabpage.id)"
                    class="flex-1 px-4 py-3 text-sm font-medium text-center relative"
                    :class="[
                        activeTab === tabpage.id
                        ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    ]"
                    >
                    <IconComponent :name="tabpage.icon" class="float-left" />
                    {{ tabpage.label }}
                    </button>
                </template>
                <template v-for="tabpage in tabList">
                    <button
                    :key="tabpage.id"
                    v-if="tabpage.checkPermission == false || (tabpage.permission && CommonFunctions.hasPermission(props.permissions, tabpage.permission))"
                    @click="switchTab(tabpage.id)"
                    class="flex-1 px-4 py-3 text-sm font-medium text-center relative"
                    :class="[
                        activeTab === tabpage.id
                        ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    ]"
                    >
                    <IconComponent :name="tabpage.icon" class="float-left" />
                    {{ tabpage.label }}
                    </button>
                </template>
                </div>
            </div>

            <!-- Tabs Content -->
            <div class="flex-1 overflow-y-auto p-4">

                <template v-for="tab in tabs" :key="tab.id">
                <div v-show="activeTab === tab.id">
                <slot :name="tab.id"></slot>
                </div>
            </template>

                <!-- Customer History Tab -->
                <div v-if="activeTab === 'activity-log'" class="space-y-4" id="extra-info-exchange-rates">
                <UserActivityLog :record="record" />
                </div>

                <!-- Audit Log Tab -->
                <div v-else-if="activeTab === 'audit-log'" class="space-y-4" id="extra-info-activity-log">
                <div
                    v-for="(log, index) in auditLogs"
                    :key="index"
                    class="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
                >
                    <div class="flex items-start gap-3">
                    <span class="text-2xl">{{ log.icon }}</span>
                    <div>
                        <p class="font-medium text-gray-900 dark:text-white">{{ log.action }}</p>
                        <p class="text-sm text-gray-600 dark:text-gray-400">{{ log.details }}</p>
                        <div class="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>{{ log.user }}</span>
                        <span>•</span>
                        <span>{{ log.date }}</span>
                        </div>
                    </div>
                    </div>
                </div>
                </div>
            </div>

        </div>

        <!-- Empty State -->
        <div v-else class="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
          Select a record to view details
        </div>
      </template>
      
      <!-- Collapsed State -->
      <div v-else class="h-full flex flex-col items-center pt-0 text-gray-400">
        <div class="writing-vertical text-sm">
          <button
            v-if="isCollapsed"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 flex items-center gap-2"
            @click="toggleCollapse">Details
        </button>
    </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.writing-vertical {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
}
</style>